import { Location } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, Params, Router } from '@angular/router';
import { UserpilotService } from '@shared/services/userpilot.service';
import { filter } from 'rxjs';

type IgniteToolStep = 'form' | 'results';

/**
 * Service to manage the step and tab query params in the URL (for better UserPilot page tagging).
 * Implemented as a dedicated service with state rather than other components/services being responsible for their own params, because race
 * conditions can cause params to go stale.
 *
 * The params are written with `Location.replaceState`, not `router.navigate`. They exist only for UserPilot, and a router navigation re-emits
 * every `queryParams` subscription and fires every `NavigationEnd` listener in the app - which previously started a second affordability
 * search, scrolled the page to the top on tab changes and re-applied deep-link values onto the search model. `replaceState` changes the
 * address bar without the router knowing, so nothing else reacts. UserPilot is told about the change directly instead.
 */
@Injectable({
  providedIn: 'root',
})
export class IgniteUrlQueryService {
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly userpilotService = inject(UserpilotService);

  private step: IgniteToolStep | undefined;
  private tab: string | null = null;
  private isApplyScheduled = false;

  /** An update was requested while the router was not ready or mid-navigation, so it is applied once that navigation settles. */
  private isApplyPending = false;

  /** The path (without query string) the params were last written to, so they can be restored after a navigation on the same page. */
  private appliedPath: string | undefined;

  public constructor() {
    this.router.events
      .pipe(filter(routerEvent => [NavigationEnd, NavigationCancel, NavigationError].some(t => routerEvent instanceof t)))
      .subscribe(() => {
        // The router does not know about params written with replaceState, so any real navigation on the same page (e.g. one using
        // queryParamsHandling: 'merge') writes the URL back without them. Put them back. On a different page, only apply an update that
        // page has asked for - otherwise leaving Ignite would carry ?step= onto unrelated pages.
        if (this.isApplyPending || this.currentPath() === this.appliedPath) {
          this.replaceUrl();
        }
      });
  }

  public setStep(step: IgniteToolStep): void {
    this.step = step;
    this.scheduleApplyQueryParams();
  }

  public setTab(tab: string | null): void {
    this.tab = tab;
    this.scheduleApplyQueryParams();
  }

  /**
   * Queues an update to the query params as a microtask.
   * Using microtask ensures that if setStep and setTab are called simultaneously, applyQueryParams is only called once so there is no race
   * condition which causes one of those params to go stale and use the previous value.
   */
  private scheduleApplyQueryParams(): void {
    if (this.isApplyScheduled) {
      return;
    }

    this.isApplyScheduled = true;
    queueMicrotask(() => {
      this.isApplyScheduled = false;
      this.applyQueryParams();
    });
  }

  private applyQueryParams(): void {
    if (!this.step) {
      return;
    }

    // On first navigation, this can run before router has committed the initial route, and a navigation might be in progress (e.g. when
    // adding affordability to a product search). Either would overwrite the URL written here, so wait for it to settle - the router events
    // subscription above applies the latest values then.
    if (!this.router.navigated || this.router.currentNavigation()) {
      this.isApplyPending = true;
      return;
    }

    this.replaceUrl();
  }

  private replaceUrl(): void {
    this.isApplyPending = false;

    const step = this.step;
    if (!step) {
      return;
    }

    const currentUrl = this.location.path(true);
    const urlTree = this.router.parseUrl(currentUrl);

    // The tab param is only applicable when on the results step
    const tab = step === 'results' ? this.tab : null;

    const queryParams: Params = { ...urlTree.queryParams, step };
    if (tab) {
      queryParams.tab = tab;
    } else {
      delete queryParams.tab;
    }
    urlTree.queryParams = queryParams;

    this.appliedPath = this.currentPath();

    const newUrl = this.router.serializeUrl(urlTree);
    if (newUrl === currentUrl) {
      return;
    }

    // Keep the router's history state (navigationId) so back/forward still behave as the router expects.
    this.location.replaceState(newUrl, '', this.location.getState());
    this.userpilotService.reload();
  }

  private currentPath(): string {
    return this.location.path().split('?')[0];
  }
}
